import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule, NgControl } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, retry, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

import { Breakpoint, BreakpointState, MediaQueryService } from '../../services/media-query.service';
import { BackendError } from '../../domain/error.domain';
import { MarketType, MarketTypeHelper } from '../../domain/market.domain';
import { SecuritySymbol, SecurityInfo, SymbolType, SecuritySymbolHelper } from '../../domain/symbol.domain';
import { AutoSuggestFilterBy, AutoSuggestSymbol, SymbolLookupDataSource } from '../../domain/symbol-lookup.domain';
import { ValueAccessorProviderFactory } from '../../components/value-accessor/value-accessor-provider-factory';
import { BaseValueAccessor } from '../../components/value-accessor/base-value-accessor';
import { NewTypeAheadComponent } from '../../components/typeahead/new-typeahead.component';
import { DropdownItemComponent } from '../../components/dropdown/dropdown-item.component';
import { IconButtonComponent } from '../../components/icon-button/icon-button.component';
import { IconComponent } from '../../components/icon/icon.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { SymbolLookupItemLabelComponent } from './symbol-lookup-item-label.component';
import { SymbolLookupService } from './symbol-lookup.service';

@Component({
  selector: 'td-wb-symbol-lookup',
  templateUrl: 'symbol-lookup.component.html',
  styleUrls: ['symbol-lookup.component.scss'],
  providers: [ValueAccessorProviderFactory(SymbolLookupComponent)],
  imports: [
    NgClass,
    NewTypeAheadComponent,
    TranslatePipe,
    TranslateDirective,
    FormsModule,
    DropdownItemComponent,
    SymbolLookupItemLabelComponent,
    NgTemplateOutlet,
    IconButtonComponent,
    IconComponent,
    CdkOverlayOrigin
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SymbolLookupComponent<T extends AutoSuggestSymbol> extends BaseValueAccessor<T> implements OnInit {
  private static readonly MAX_RESULTS_DEFAULT: number = 7;
  private static readonly MAX_RESULTS_COMPACT: number = 5;
  @Input() name: string;
  @Input() lenient: boolean;
  @Input() maxResults: number = SymbolLookupComponent.MAX_RESULTS_DEFAULT;
  @Input() maxResultsCompact: number = SymbolLookupComponent.MAX_RESULTS_COMPACT;
  @Input({ transform: (value: string): MarketType => MarketTypeHelper.fromString(value) }) market: MarketType;
  @Input() symbolTypes: SymbolType | Array<SymbolType>;
  @Input() disabled: boolean = false;
  @Input() debounce: number = 300;
  @Input() dataSource: SymbolLookupDataSource;
  @Input() placeholderKey: string;
  @Input() customTemplate: TemplateRef<unknown>;
  @Input() preSelectFirstItem: boolean = true;
  @Input() recentSymbolSearch: boolean = false;
  @Input() fractionalSymbolSearch: boolean = false;
  @Input() filterBy: AutoSuggestFilterBy | undefined = undefined;

  @Output() readonly onSelect: EventEmitter<SecurityInfo> = new EventEmitter<SecurityInfo>();
  @Output() readonly onInputChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() readonly onHoverOrActive: EventEmitter<{ hoverState: boolean; focusState: boolean }> = new EventEmitter<{ hoverState: boolean; focusState: boolean }>();

  @HostBinding('class.td-wb-symbol-lookup--hover') hoverState: boolean = false;
  @HostBinding('class.td-wb-symbol-lookup--focus') focusState: boolean = false;
  @ViewChild(NewTypeAheadComponent) typeAhead: NewTypeAheadComponent<SecuritySymbol | AutoSuggestSymbol>;
  focusOnInput: boolean = false;
  checkSymbol: boolean = false;
  symbolInput: string;
  symbols: Array<AutoSuggestSymbol> = [];
  compactSymbolLabel: boolean = false;
  isRecentSearch: boolean = false;
  ngControl: NgControl;
  recentSearchResults: Array<AutoSuggestSymbol> = [];
  private readonly symbolInputValueChange: Subject<string> = new Subject<string>();
  private currentMaxResult: number = SymbolLookupComponent.MAX_RESULTS_DEFAULT;

  constructor(@Inject(SymbolLookupService) private defaultDataSource: SymbolLookupDataSource,
              @Inject(Injector) private injector: Injector,
              private mediaQueryService: MediaQueryService,
              private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.setupSymbolInputValueChangeObserver();

    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        // assuming maxResultsCompact and maxResults will never change  after component has been initialized
        this.currentMaxResult = breakpointState.matches ? this.maxResultsCompact : this.maxResults;
        this.compactSymbolLabel = breakpointState.matches;
        this.changeDetectorRef.markForCheck();
      });
  }

  // Listens to focusout event on symbol input as blur wont work
  // Focus out on host receives every focusout of children, on this case input and button
  // so we filter for 2 instances
  //    when user types well-formed string and clicks out but NOT tabbing to button (relatedTarget === null)
  // OR types well formed string but tabs out from input and button
  @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent): void {
    const isValidSymbol = this.isValidSymbol(this.symbolInput);
    if (event.target instanceof HTMLElement) {
      if ((isValidSymbol && !this.checkSymbol && event.target.id === 'symbolInput' && event.relatedTarget === null) ||
        (isValidSymbol && !this.checkSymbol && event.target.getAttribute('type') === 'button')) {
        this.onSymbolButtonClicked();
      }
    }
    this.focusState = false;
    this.emitHoverOrActiveState();
  }

  @HostListener('focusin') onFocus(): void {
    this.focusState = true;
    this.emitHoverOrActiveState();
  }

  @HostListener('mouseenter') onHover(): void {
    this.hoverState = true;
    this.emitHoverOrActiveState();
  }

  @HostListener('mouseleave') onHoverOut(): void {
    this.hoverState = false;
    this.emitHoverOrActiveState();
  }

  isActive(): boolean {
    return this.hoverState || this.focusState;
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl);
  }

  querySymbols(symbolQuery: string): void {
    if (symbolQuery === undefined) {
      this.symbols = [];
    } else {
      this.symbolInputValueChange.next(symbolQuery);
    }
  }

  checkRecentSearchFeatureFlag(inputValue: string): boolean {
    return (inputValue === undefined || inputValue === '') && this.recentSymbolSearch;
  }

  displayLabelRecentSearches(inputValue: string): boolean {
    return this.isRecentSearch && this.checkRecentSearchFeatureFlag(inputValue);
  }

  setRecentSearches(inputValue: string): void {
    this.focusOnInput = true;
    if (this.checkRecentSearchFeatureFlag(inputValue)) {
      this.getRecentSearches().subscribe({
        next: (result: Array<T>): void => {
          if (result.length > 0) {
            this.symbols = result;
            this.isRecentSearch = true;
            if (!this.typeAhead.isOpen() && this.focusOnInput && !this.typeAhead.isSkipOpenOnFocus() && (this.symbolInput === null || this.symbolInput === '')) {
              // Only open if it's not already open AND we still have focus AND typeahead is not skipping open
              // AND input value is empty (Recent Searches scenario)
              this.typeAhead.open();
            }
          }
          this.changeDetectorRef.markForCheck();
        },
        error: (errorResponse: Array<BackendError>): void => {
          if (errorResponse) {
            this.symbols = [];
            this.changeDetectorRef.markForCheck();
          }
        }
      });
    }
  }

  inputChange(inputValue: string): void {
    if (inputValue === undefined || inputValue === '') {
      this.resetCachedValues();
      this.onInputChange.emit(inputValue);
      this.updateValidity();
      this.symbolInputValueChange.next(''); // when input is empty trigger reset on searched items
    } else {
      this.querySymbols(inputValue);
      if (inputValue !== undefined && this.lenient) {
        this.setModel({ symbol: inputValue } as T);
      }
      if (this.checkSymbol && !this.isValidSymbol(inputValue)) {
        this.setModel(undefined);
        this.checkSymbol = false;
      }
      this.onInputChange.emit(inputValue);
      this.updateValidity();
    }
  }

  inputBlur(): void {
    this.focusOnInput = false;
  }

  isDisabled(): boolean {
    return this.disabled;
  }

  // either from lost focus or button clicked.
  // input value might not be correct
  onSymbolButtonClicked(): void {
    if (!this.disabled && this.isNotEmptyInputValue() && this.symbols.length > 0) {
      this.validateAndInvokeOnSelect('button');
    } else if (this.isNotEmptyInputValue() && this.symbols.length === 0 && this.ngControl.control !== undefined) {
      this.ngControl.control.markAsTouched();
    }
    this.checkSymbol = true;
    this.updateValidity();
  }

  updateValidity(): void {
    if (this.ngControl.control !== undefined) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.changeDetectorRef.markForCheck();
  }

  // selection happened from the dropdown, always trust it
  onSelectSymbol(selection: { selected: SecuritySymbol | AutoSuggestSymbol }, from: string): void {
    if (from !== undefined && selection !== undefined) {
      this.setModel(selection.selected);
      this.checkSymbol = true;
      this.onSelect.emit(selection.selected as SecurityInfo);
      this.symbols = [];
      this.changeDetectorRef.markForCheck();
    }
  }

  writeValue(value: T): void {
    const inputValue = this.symbolToString(value);
    if (this.symbolInput !== inputValue) {
      this.symbolInput = inputValue;
      super.writeValue(value === null ? undefined : value);
      if (inputValue !== null && inputValue !== undefined && inputValue !== '' && this.isValidSymbol(inputValue)) {
        this.checkSymbol = true;
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Reset previously retrieved values.
   */
  resetCachedValues(): void {
    this.writeValue(null);
    this.ngControl.control.reset();
    this.symbols = [];
    this.updateValidity();
  }

  private symbolToString(value: T): string {
    if (value === undefined || value === null) {
      return '';
    }
    return SymbolLookupService.symbolToString(value);
  }

  private setupSymbolInputValueChangeObserver(): void {
    this.symbolInputValueChange
      .pipe(
        debounceTime(this.debounce),
        switchMap((symbolQuery: string): Observable<Array<AutoSuggestSymbol>> => {
          // prevents issue when holding backspace was receiving an older, delayd string value instead of empty string
          if (symbolQuery === '') {
            // below condition added to preserve recent search symbols when focus on the symbol lookup input box
            if (this.isRecentSearch) {
              return of(this.symbols);
            }
            return of([]);
          }
          return this.doSearch(symbolQuery).pipe(
            // if the search fails, retry the request 2 times
            retry(2),
            // if it still fails, catch the error to prevent the main stream from dying
            catchError(() => {
              // return an empty array to clear the results and allow for future searches
              return of([]);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (result: Array<AutoSuggestSymbol>): void => {
          this.symbols = result;
          // below condition added to preserve recent search symbols when focus on the symbol lookup input box
          if (this.symbolInput !== '') {
            this.isRecentSearch = false;
          }
          // Commented out since goalassist clients are able to sell a non-TD ETF that they hold on their portfolio
          // As a direct consequence, on trading, when picking a symbol then later pasting a well-formed but nonexistent symbol,
          //   it will not clear symbol model, not fire invalid error, and quote component wont refresh,
          //   so hitting send will trade old symbol
          // if (result.length === 0 && this.checkSymbol) {
          //   this.checkSymbol = false;
          //   this.setModel(undefined);
          // }
          this.changeDetectorRef.markForCheck();
        }
        // no error handler is needed here, as it's handled in the stream now
      });
  }

  private doSearch(symbolQuery: string): Observable<Array<AutoSuggestSymbol>> {
    if (this.dataSource !== undefined) {
      return this.dataSource.search<AutoSuggestSymbol>(symbolQuery,
        this.currentMaxResult,
        this.symbolTypes,
        this.market,
        this.fractionalSymbolSearch,
        this.filterBy
      );
    }
    return this.defaultDataSource.search<AutoSuggestSymbol>(
      symbolQuery,
      this.currentMaxResult,
      this.symbolTypes,
      this.market,
      this.fractionalSymbolSearch,
      this.filterBy
    );
  }

  private getRecentSearches(): Observable<Array<T>> {
    if (this.dataSource !== undefined) {
      return this.dataSource.getRecentSymbols(this.currentMaxResult, this.symbolTypes, this.market);
    }
    return this.defaultDataSource.getRecentSymbols(this.currentMaxResult, this.symbolTypes, this.market);
  }

  private setModel(symbol: SecuritySymbol | AutoSuggestSymbol, overwrite: boolean = true): void {
    if (overwrite) {
      this.value = symbol as T;
    } else if (this.value === undefined || this.value === null ||
      (symbol !== undefined && this.isSecuritySymbol(this.value) &&
        (symbol.symbol !== this.value.symbol || symbol.marketType !== this.value.marketType))) {
      this.value = symbol as T;
    }
  }

  private isSecuritySymbol(symbol: SecuritySymbol | AutoSuggestSymbol): symbol is SecuritySymbol {
    return (symbol as SecuritySymbol).symbol !== undefined;
  }

  private isNotEmptyInputValue(): boolean {
    return this.symbolInput !== null && this.symbolInput !== undefined && this.symbolInput !== '';
  }

  private validateAndInvokeOnSelect(source: string): void {
    let newSymbol: SecuritySymbol | AutoSuggestSymbol;
    if (this.isNotEmptyInputValue()) {
      if (this.isValidSymbol(this.symbolInput)) {
        // when user has a well-formed string and focus out/click search (instead of picking from type-ahead)
        // we will first try picking from first item on the last autosuggest list received,
        if (this.symbols !== undefined && this.symbols.length > 0) {
          newSymbol = this.symbols[0];
        } else { // otherwise fallback to parsing string - which will contain minimal info {symbol, market & marketType}
          newSymbol = SecuritySymbolHelper.parse(this.symbolInput);
        }
      } else {
        if (this.lenient) {
          this.setModel({ symbol: this.symbolInput } as T);
        } else {
          this.setModel(undefined);
        }
      }
    }

    this.onSelectSymbol({ selected: newSymbol }, source);
  }

  private isValidSymbol(symbolString: string): boolean {
    let symbol: SecuritySymbol;
    const isValid = SecuritySymbolHelper.isValid(symbolString, this.symbolTypes);
    if (isValid && this.market !== undefined) {
      symbol = SecuritySymbolHelper.parse(symbolString);
      if (this.market !== symbol.market) {
        return false;
      }
    }
    return isValid;
  }

  private emitHoverOrActiveState(): void {
    this.onHoverOrActive.emit({ hoverState: this.hoverState, focusState: this.focusState });
    this.changeDetectorRef.markForCheck();
  }
}
